import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import Navbar from '../components/navbar';
import { authApi } from '../api/authApi';

type Status = 'verifying' | 'success' | 'error';

export type VerificationMethod = 'email' | 'phone';

interface VerifyPageCopy {
    title: string;
    verifying: string;
    success: string;
    defaultError: string;
    missingToken: string;
}

const COPY: Record<VerificationMethod, VerifyPageCopy> = {
    email: {
        title: 'Verify Email',
        verifying: 'Verifying your email…',
        success: 'Your email has been verified. Redirecting you to sign in…',
        defaultError: 'Email verification failed.',
        missingToken: 'Missing verification token.',
    },
    phone: {
        title: 'Verify Phone',
        verifying: 'Verifying your phone number…',
        success: 'Your phone number has been verified. Redirecting you to sign in…',
        defaultError: 'Phone verification failed.',
        missingToken: 'Missing verification code.',
    },
};

interface VerifyPageProps {
    method: VerificationMethod;
}

export default function VerifyPage({ method }: VerifyPageProps) {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') ?? params.get('code') ?? '';
    const copy = COPY[method];

    const [status, setStatus] = useState<Status>('verifying');
    const [errorMessage, setErrorMessage] = useState('');
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        if (!token) {
            setStatus('error');
            setErrorMessage(copy.missingToken);
            return;
        }

        (async () => {
            try {
                if (method === 'email') {
                    await authApi.verifyEmail(token);
                } else {
                    await authApi.verifyPhone(token);
                }
                setStatus('success');
                setTimeout(() => navigate('/signin?verified=1', { replace: true }), 1500);
            } catch (err: any) {
                setStatus('error');
                setErrorMessage(err?.message || copy.defaultError);
            }
        })();
    }, [token, method, copy, navigate]);

    return (
        <>
            <Navbar />
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {copy.title}
                    </Typography>

                    {status === 'verifying' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                            <CircularProgress />
                            <Typography variant="body1">{copy.verifying}</Typography>
                        </Box>
                    )}

                    {status === 'success' && (
                        <>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {copy.success}
                            </Alert>
                            <Button variant="contained" onClick={() => navigate('/signin?verified=1', { replace: true })}>
                                Continue to Sign In
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Alert>
                            <Button variant="contained" onClick={() => navigate('/signin', { replace: true })}>
                                Go to Sign In
                            </Button>
                        </>
                    )}
                </Paper>
            </Container>
        </>
    );
}
